import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { Notifications } from '@mantine/notifications';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { theme, cssVariablesResolver } from './theme';
import { ProtectedRoute, ErrorBoundary, PageLoadingFallback } from './components/common';
import { AppLayout } from './components/layout';
import { queryClient, persistOptions } from './config/queryClient';
import { useAnalyticsPageView } from './hooks/useAnalyticsPageView';
import 'dayjs/locale/ja';

// Mantine CSSのインポート
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

// ページコンポーネントの遅延読み込み
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
const FieldListPage = lazy(() =>
  import('./pages/FieldListPage').then((m) => ({ default: m.FieldListPage }))
);
const FieldDetailPage = lazy(() =>
  import('./pages/FieldDetailPage').then((m) => ({ default: m.FieldDetailPage }))
);

/**
 * ルーター内部のコンテンツ
 * useLocation を使って ErrorBoundary の resetKey にパス名を渡す
 */
export function AppRoutes() {
  const location = useLocation();
  useAnalyticsPageView();

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* 公開ルート */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* 保護されたルート */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Navigate to="/fields" replace />} />
              <Route path="/fields" element={<FieldListPage />} />
              <Route path="/fields/:fieldId" element={<FieldDetailPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * アプリケーションのルートコンポーネント
 * ルーティングとグローバルプロバイダーを設定
 */
function App() {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver}>
        <DatesProvider settings={{ locale: 'ja' }}>
          <Notifications position="top-right" />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </DatesProvider>
      </MantineProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </PersistQueryClientProvider>
  );
}

export default App;
